import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Stack, TextField, Typography } from '@mui/material';
import { theme } from '../../../constants/theme';
import { useThemeContext } from '../../../hook/useThemeManager';
import Cancel from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import { IReedemableCode, IUserSummary } from '../../../types/global/helper';
import { ShortSegmentedControl } from '../../SegmentedControl/ShortSegmentedControl';
import { FONT_ENUM } from '../../../types/global/fonts.types';
import { ThspianAvatar } from '../../Avatar/ThspianAvatar';
import { Spinner } from '@chakra-ui/react';
import { replaceRouteParam } from '../../../utils/routesProcessor';
import { navigate } from '../../../redux/navigator/action';
import { ROUTES } from '../../../types/global/routes.types';
import { AvatarShimmer } from '../EsMyProfileContainer/AvatarShimmer';
import {
    alreadyAssignedRedeemableCodesSelector,
    fetchingAssignedCodesSelector,
    searchingUserSearchSelector,
    sendingRedeemableCodesSelector,
    userSearchSelector,
} from '../../AvatarGroup/selector';
import CustomButton from '../../Button/CustomButton';
import { searchUser } from '../../../redux/user/actions';
import { assignRedeemableCode, getRedeemableCodes } from '../../../redux/contest/actions';
import { useDispatchPromise } from '../../../utils/AppUtils';
import { isEmpty } from '../../../utils/lodash';
import { contestRequestsSelector, gettingUserRequests } from '../../../pages/ManageCampaign/selectors';
import { IContestRequest, TRequestStatus } from '../../../redux/requests/types';
import { getRequests, resolveRequest } from '../../../redux/requests/action';
import { ReasonsSelector } from './ReasonsSelector';

export interface IFollowModal {
    placeholder?: string;
    open: boolean;
    onClose?: () => void;
    externalUserId?: string;
    isExternalUser?: boolean;
    contestId: string;
}

/**
 *
 * @param param0
 * @returns
 *
 * I will attempt to handle the api call and redux operation for this module inside here
 * As I see that it only has one functionality, which is to search and select users.
 * In the future we will move it out of here to the parent to keep it uniform.
 */

const tabList = ['assign', 'assigned', 'requests'];
type TabSelector = 'assign' | 'assigned' | 'requests';
const RedeemableCodeDialogue: React.FC<IFollowModal> = ({ placeholder, open, onClose, contestId }) => {
    const [, setSearchValue] = useState('');
    const [visibleTab, setVisibleTab] = useState<TabSelector>('assign');
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const promiseDispatch = useDispatchPromise();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);
    const redeemableCodes = useSelector(alreadyAssignedRedeemableCodesSelector);
    const userRequests = useSelector(contestRequestsSelector);
    const gettingRequests = useSelector(gettingUserRequests);
    const fetchingAssignedCodes = useSelector(fetchingAssignedCodesSelector);
    const sendingCode = useSelector(sendingRedeemableCodesSelector);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [filteredResult, setFilteredResult] = useState<IReedemableCode[]>([]);
    const [filteredRequests, setFilteredRequests] = useState<IContestRequest[]>([]);
    const [selectedUser, setSelectedUser] = useState('');
    const [selectedRequest, setSelectedRequest] = useState<IContestRequest>();
    const [openReasonDialogue, setOpenReasonDialogue] = useState(false);

    const handleAvatarClick = (avatar: IUserSummary) => {
        const profileRoute = replaceRouteParam(ROUTES.ESMY_PROFILE, 'profileId?', avatar.userName);
        dispatch(navigate({ routes: profileRoute }));
        onClose && onClose();
    };

    const handleDebouncedSearch = useRef(
        _.debounce((searchTerm: string) => {
            if (searchTerm.length > 4 && !searchingUser) {
                dispatch(searchUser({ searchTerm }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: { target: { value: string } }) => {
        if (visibleTab === 'assign') {
            setSearchValue(event.target.value);
            handleDebouncedSearch(event.target.value);
        }
        if (visibleTab === 'assigned') {
            const filtered = redeemableCodes.filter(
                code =>
                    code.user.firstName.startsWith(event.target.value) ||
                    code.user.lastName.startsWith(event.target.value) ||
                    code.user.userName.startsWith(event.target.value),
            );
            setFilteredResult(filtered);
        }

        if (visibleTab === 'requests') {
            const filtered = userRequests.filter(
                code =>
                    code.user.firstName.startsWith(event.target.value) ||
                    code.user.lastName.startsWith(event.target.value) ||
                    code.user.userName.startsWith(event.target.value),
            );
            setFilteredRequests(filtered);
        }
    };

    const handleGetAssignedCodes = useCallback(() => {
        dispatch(
            getRedeemableCodes({
                contestId,
                limit: 100,
                page: 1,
            }),
        );
    }, [contestId, dispatch]);

    const handleGetContestRequests = useCallback(() => {
        dispatch(
            getRequests({
                contestId: contestId,
                limit: 100,
                page: 1,
            }),
        );
    }, [contestId, dispatch]);

    useEffect(() => {
        if (visibleTab === 'assigned') {
            setFilteredResult(redeemableCodes);
        }
        if (visibleTab === 'requests') {
            setFilteredRequests(userRequests);
        }
    }, [visibleTab, redeemableCodes, userRequests]);

    const handleChangeTab = (tab: string) => {
        setVisibleTab(tab as TabSelector);
        setSearchValue('');
        if (tab === 'assigned') {
            handleGetAssignedCodes();
        }
        if (tab === 'requests') {
            handleGetContestRequests();
        }
    };

    const handleSendCode = (userId: string) => {
        setSelectedUser(userId);
        promiseDispatch(
            assignRedeemableCode({
                contestId,
                userId,
            }),
        )
            .then(() => {
                setSelectedUser('');
            })
            .catch(() => {
                setSelectedUser('');
            });
    };

    const shouldDisableButton = (userId: string) =>
        !!redeemableCodes && !isEmpty(redeemableCodes) && redeemableCodes.some(code => code.user._id === userId);

    const handleResolveRequest = (request: IContestRequest, status: TRequestStatus, reason?: string) => {
        promiseDispatch(
            resolveRequest({
                reason: reason as string,
                request_status: status,
                requestId: request._id,
            }),
        )
            .then((response: any) => {
                if (status === 'approved') {
                    handleSendCode(response.user._id);
                }
            })
            .catch(e => {});
    };

    const shouldDisablResolution = (req: IContestRequest) => !req.resolved;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            color="blue"
            PaperProps={{
                style: {
                    backgroundColor: theme[themeStyle].deep_grey_lighter,
                    overflowY: 'scroll',
                    border: '1px solid grey',
                    maxHeight: '100vh',
                    margin: 0,
                },
                sx: {
                    height: {
                        xs: '100vh',
                        sm: '70%',
                        md: '50%',
                    },
                    width: {
                        xs: '100vw',
                        sm: '50%',
                        md: '40%',
                    },
                    borderRadius: {
                        xs: 3,
                        sm: 2,
                        md: 5,
                    },
                },
            }}>
            <ReasonsSelector
                open={openReasonDialogue}
                handleOnClose={() => setOpenReasonDialogue(!openReasonDialogue)}
                onChange={reasons => !!selectedRequest && handleResolveRequest(selectedRequest, 'denied', reasons)}
            />
            <ShortSegmentedControl
                tabs={tabList}
                onHandleClick={handleChangeTab}
                sx={{
                    width: '100%',
                }}
                tabStyle={{
                    width: '100%',
                }}
                buttonStyle={{
                    width: '100%',
                }}
            />
            <TextField
                autoFocus
                variant="outlined"
                placeholder={placeholder ?? 'Search users...'}
                color="secondary"
                InputProps={{
                    placeholder: placeholder ?? `Search ${visibleTab}...`,
                    style: {
                        color: theme[themeStyle].tag_white, // Change the placeholder color to white
                        border: '0.5px solid white', // Set the border width and color
                        borderRadius: '50px', // Set the border radius
                        paddingLeft: '8px', // Add some padding for the text inside the input field
                        fontSize: 12,
                        minWidth: 200,
                    },
                }}
                inputProps={{
                    color: 'blue',
                }}
                // Add your search functionality here
                onChange={e => {
                    handleSearchInputChange(e);
                }}
                sx={{
                    px: 3,
                    pt: 3,
                    pb: 1,
                }}
            />
            <DialogContent ref={scrollRef}>
                {visibleTab === 'assign' && (
                    <>
                        {searchingUser && new Array(20).fill('X').map((_, i) => <AvatarShimmer key={i} />)}
                        {!!userResults &&
                            !searchingUser &&
                            !_.isEmpty(userResults) &&
                            userResults.map((user, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}
                                    mb={1}>
                                    <Stack flexDirection="row" alignItems="center">
                                        <ThspianAvatar
                                            key={index}
                                            user={user as unknown as IUserSummary}
                                            onClick={() => handleAvatarClick(user as unknown as IUserSummary)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '50%',
                                                marginRight: '10px',
                                                height: 60,
                                                width: 60,
                                            }}
                                        />
                                        <Stack direction="column">
                                            <Typography
                                                style={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 13,
                                                }}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Typography
                                                color="white"
                                                style={{
                                                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                    fontSize: 10,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                @{user.userName}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <CustomButton
                                        onClick={() => handleSendCode(user._id)}
                                        disabled={shouldDisableButton(user._id)}
                                        customStyle={{
                                            width: '100px',
                                        }}
                                        loading={selectedUser === user._id && sendingCode}
                                        label={shouldDisableButton(user._id) ? ' Code Sent' : 'Send Code'}
                                        textStyle={{
                                            fontSize: 12,
                                        }}
                                    />
                                </Stack>
                            ))}
                    </>
                )}
                {visibleTab === 'assigned' && (
                    <>
                        {fetchingAssignedCodes && new Array(20).fill('X').map((_, i) => <AvatarShimmer key={i} />)}
                        {!!filteredResult &&
                            !fetchingAssignedCodes &&
                            !_.isEmpty(filteredResult) &&
                            filteredResult.map(({ user }, index) => (
                                <Stack
                                    flexDirection="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    key={index}
                                    mb={1}>
                                    <Stack flexDirection="row" alignItems="center">
                                        <ThspianAvatar
                                            key={index}
                                            user={user as IUserSummary}
                                            onClick={() => handleAvatarClick(user as IUserSummary)}
                                            sx={{
                                                cursor: 'pointer',
                                                borderRadius: '50%',
                                                marginRight: '10px',
                                                height: 60,
                                                width: 60,
                                            }}
                                        />
                                        <Stack direction="column">
                                            <Typography
                                                style={{
                                                    color: theme[themeStyle].tag_white,
                                                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                    fontSize: 13,
                                                }}>
                                                {user.firstName} {user.lastName}
                                            </Typography>
                                            <Typography
                                                color="white"
                                                style={{
                                                    fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                    fontSize: 10,
                                                    color: theme[themeStyle].tag_white,
                                                }}>
                                                @{user.userName}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            ))}
                    </>
                )}

                {visibleTab === 'requests' && (
                    <>
                        {gettingRequests && new Array(20).fill('X').map((_, i) => <AvatarShimmer key={i} />)}
                        {!!filteredRequests &&
                            !gettingRequests &&
                            !_.isEmpty(filteredRequests) &&
                            filteredRequests
                                .filter(e => !e.resolved)
                                .map((req, index) => {
                                    const { user } = req;
                                    return (
                                        <Stack
                                            flexDirection="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            key={index}
                                            mb={1}>
                                            <Stack flexDirection="row" alignItems="center">
                                                <ThspianAvatar
                                                    key={index}
                                                    user={user as IUserSummary}
                                                    onClick={() => handleAvatarClick(user as IUserSummary)}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        borderRadius: '50%',
                                                        marginRight: '10px',
                                                        height: 60,
                                                        width: 60,
                                                    }}
                                                />
                                                <Stack direction="column">
                                                    <Typography
                                                        style={{
                                                            color: theme[themeStyle].tag_white,
                                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                                            fontSize: 13,
                                                        }}>
                                                        {user.firstName} {user.lastName}
                                                    </Typography>
                                                    <Typography
                                                        color="white"
                                                        style={{
                                                            fontFamily: FONT_ENUM.REGULAR_HEADER,
                                                            fontSize: 10,
                                                            color: theme[themeStyle].tag_white,
                                                        }}>
                                                        @{user.userName}
                                                    </Typography>
                                                </Stack>
                                            </Stack>
                                            <Stack flexDirection="row" alignItems="center" gap={'10px'}>
                                                <CustomButton
                                                    onClick={() => handleResolveRequest(req, 'approved')}
                                                    disabled={!shouldDisablResolution(req)}
                                                    customStyle={{
                                                        width: '100px',
                                                    }}
                                                    label={'Approve'}
                                                    textStyle={{
                                                        fontSize: 12,
                                                    }}
                                                />
                                                <CustomButton
                                                    onClick={() => {
                                                        setSelectedRequest(req);
                                                        setOpenReasonDialogue(!openReasonDialogue);
                                                    }}
                                                    disabled={!shouldDisablResolution(req)}
                                                    customStyle={{
                                                        width: '100px',
                                                    }}
                                                    label={'Deny'}
                                                    textStyle={{
                                                        fontSize: 12,
                                                    }}
                                                />
                                            </Stack>
                                        </Stack>
                                    );
                                })}
                    </>
                )}
            </DialogContent>

            {false && <Spinner pos="absolute" color="white" size="xl" right={'50%'} bottom={10} h={5} w={5} />}

            <DialogActions sx={{ position: 'absolute', right: 10, bottom: 10 }}>
                <IconButton
                    color="primary"
                    aria-label="Add Avatar"
                    onClick={onClose}
                    style={{
                        borderRadius: '50%',
                        borderWidth: 1,
                        borderColor: theme[themeStyle].lightWhite,
                        width: 30,
                    }}>
                    <Cancel color="secondary" />
                </IconButton>
            </DialogActions>
        </Dialog>
    );
};

export { RedeemableCodeDialogue };
